import { Box, Stack } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState } from 'react';

import { AuthLegalNotice } from '#modules/user/features/auth/components/AuthLegalNotice';
import { AuthMarqueePreview } from '#modules/user/features/auth/components/AuthMarqueePreview';
import { AuthPageLayout } from '#modules/user/features/auth/components/AuthPageLayout';
import { OAuthProvider, OAuthProviders } from '#modules/user/features/auth/constants/providers';
import { ReCaptchaCaption } from '#features/form';
import { Button, ChevronDownIcon, ChevronUpIcon, Link, PageHeading } from '#features/ui';
import { PasswordInput } from '#features/ui/components/inputs/PasswordInput';
import { TextInput } from '#features/ui/components/inputs/TextInput';

export const Route = createFileRoute('/{-$locale}/_layout/preview/sign-in')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: SignInPreview,
});

const disclosure = (label: string, open: boolean, toggle: () => void, id: string) => (
  <Button
    type="button"
    variant="ghost"
    color="fg.link"
    minH={11}
    h="auto"
    w="100%"
    py={2}
    px={12}
    position="relative"
    whiteSpace="normal"
    textAlign="center"
    aria-expanded={open}
    aria-controls={id}
    onClick={toggle}
  >
    <Box as="span" minW={0}>
      {label}
    </Box>
    <Box position="absolute" insetInlineEnd={4}>
      {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </Box>
  </Button>
);

/** Visual fixture: providers and submit never send credentials or start authentication. */
function SignInPreview() {
  const { t } = useLingui();
  const [emailOpen, setEmailOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const provider = (type: OAuthProvider) => {
    const { fullName: providerName, icon: Icon } = OAuthProviders[type];
    return (
      <Button
        key={type}
        type="button"
        size="lg"
        variant="outline"
        color="fg"
        w="100%"
        h="auto"
        minH={12}
        py={3}
        whiteSpace="normal"
      >
        <Icon style={{ flexShrink: 0 }} />
        <Box as="span" minW={0}>{t`Sign in with ${providerName}`}</Box>
      </Button>
    );
  };
  return (
    <AuthPageLayout artwork={(compact) => <AuthMarqueePreview compact={compact} />}>
      <PageHeading
        align="center"
        description={
          <>
            {t`signIn.noAccount`}{' '}
            <Link to="/{-$locale}/sign-up" color="fg.link">{t`signIn.createAccount`}</Link>
          </>
        }
      >
        {t`signIn.welcome`}
      </PageHeading>
      <Stack gap={4}>
        <Box
          textAlign="center"
          textStyle="caption"
          color="fg.muted"
        >{t`Use the method you used before.`}</Box>
        <Stack gap={3}>{[OAuthProvider.GOOGLE, OAuthProvider.DISCORD].map(provider)}</Stack>
        <Stack gap={3}>
          {disclosure(
            t`Sign in with email`,
            emailOpen,
            () => setEmailOpen(!emailOpen),
            'preview-email'
          )}
          <Stack gap={4} id="preview-email" hidden={!emailOpen}>
            <TextInput label="Email" name="preview-email" autoComplete="off" />
            <PasswordInput label={t`password`} name="preview-password" autoComplete="off" />
            <Link
              to="/{-$locale}/forgot-password"
              color="fg.link"
              textStyle="caption"
              alignSelf="flex-end"
            >{t`signIn.forgotPassword`}</Link>
            <Button type="button" w="100%">{t`signIn.signIn`}</Button>
          </Stack>
          {disclosure(
            t`Other ways to sign in`,
            otherOpen,
            () => setOtherOpen(!otherOpen),
            'preview-other'
          )}
          <Stack gap={3} id="preview-other" hidden={!otherOpen}>
            {[OAuthProvider.STEAM, OAuthProvider.EPIC_GAMES, OAuthProvider.RIOT_GAMES].map(
              provider
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack gap={2} textAlign="center" color="fg.muted">
        <ReCaptchaCaption />
        <AuthLegalNotice />
      </Stack>
    </AuthPageLayout>
  );
}
